import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const FIELDS = [
  ["company_name", "Company name"], ["email", "Email"], ["address_line1", "Address line 1"], ["address_line2", "Address line 2"],
  ["phone", "Phone #"], ["toll_free", "Toll Free #"], ["invoice_prefix", "Invoice # prefix"], ["tax_rate", "Default tax rate (%)", "number"],
  ["terms", "Payment terms"],
];

export function InvoiceSettingsForm({ settings, onSaved, onCancel }) {
  const [v, setV] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setV(settings); }, [settings]);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/invoice-settings", { ...v, tax_rate: Number(v.tax_rate) || 0 });
      toast.success("Business details saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="invoice-settings-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, type]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={`settings-${k}`}>{label}</Label>
            <Input id={`settings-${k}`} type={type || "text"} step="any" value={v[k] ?? ""} onChange={set(k)} data-testid={`settings-${k}`} />
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="settings-service_text">Default "Service Performed" text</Label>
        <Textarea id="settings-service_text" rows={3} value={v.service_text ?? ""} onChange={set("service_text")} data-testid="settings-service_text" />
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} data-testid="settings-cancel-button">Cancel</Button>}
        <Button type="submit" disabled={saving} className="hover:bg-blue-600 hover:text-white" data-testid="settings-save-button">
          {saving && <Loader2 size={14} className="animate-spin" />} Save
        </Button>
      </div>
    </form>
  );
}

export default function InvoiceSettingsDialog({ open, settings, onClose, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="invoice-settings-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Business details</DialogTitle></DialogHeader>
        <InvoiceSettingsForm settings={settings} onSaved={(d) => { onSaved(d); onClose(); }} onCancel={onClose} />
      </DialogContent>
    </Dialog>
  );
}
